import pb from '../lib/pocketbase';
import { toasts } from "svelte-toasts";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { EMAIL_API_URL } from '../config.js';
import { 
    addCoverPage,
    addHeader, 
    addQuotationInfo, 
    addClientInfo, 
    addDetailsTable, 
    addImages, 
    addFooter 
} from '../lib/pdfGenerator.js';

// Save anonymous checkout order
export async function saveAnonymousOrder(orderData) {
  try {
    console.log('Saving anonymous order:', orderData);
    
    // Create main quote record (cotiza_cab)
    const cotizaCabData = {
      numero: `COT-${Date.now()}`, // Generate unique quote number
      fecha: new Date().toISOString().split('T')[0], // Today's date
      nit: orderData.customerNIT || '', // Customer NIT
      nombre: orderData.customerName,
      telefono: orderData.customerPhone,
      correo: orderData.customerEmail,
      total: orderData.totalPrice,
      sta: true // Active quote
    };
    
    console.log('Creating cotiza_cab record:', cotizaCabData);
    const cotizaCab = await pb.collection('cotiza_cab').create(cotizaCabData);
    console.log('Cotiza_cab created:', cotizaCab.id);
    
    // Create detail records (cotiza_det) for each selected component
    const cotizaDetRecords = [];
    
    // Add parent product
    const parentDet = await pb.collection('cotiza_det').create({
      cotiza_cab: cotizaCab.id,
      producto: orderData.selectedParent,
      cantidad: 1,
      precio: 0, // Parent price will be calculated separately
      total: 0
    });
    cotizaDetRecords.push(parentDet);
    console.log('Parent product detail created:', parentDet.id);
    
    // Add selected children
    for (const child of orderData.selectedChildren) {
      const childDet = await pb.collection('cotiza_det').create({
        cotiza_cab: cotizaCab.id,
        producto: child.id,
        cantidad: child.cantidad || 1,
        precio: child.precio || 0,
        total: (child.precio || 0) * (child.cantidad || 1)
      });
      cotizaDetRecords.push(childDet);
      console.log('Child product detail created:', childDet.id);
    }
    
    console.log('Order saved successfully to cotiza_cab and cotiza_det');
    
    // Send email notification to admin
    await sendOrderNotification({
      ...orderData,
      cotiza_id: cotizaCab.id,
      cotiza_numero: cotizaCabData.numero
    });
    
    // Note: Customer email is now sent from the checkout component
    // to provide better user feedback and error handling

    return {
      cotizaCab,
      cotizaDet: cotizaDetRecords
    };
    
  } catch (error) {
    console.error('Error saving anonymous order:', error);
    
    // Send error notification email
    await sendOrderNotification({
      ...orderData,
      error: error.message
    });
    
    throw error;
  }
}

// Send email notification about new order
async function sendOrderNotification(orderData) {
  try {
    const isError = orderData.error;
    const subject = isError ? 'Error en Nueva Solicitud de Configuración' : 'Nueva Solicitud de Configuración';
    
    const emailData = {
      to: 'admin@nonosky.com',
      subject: subject,
      body: `
        ${isError ? 'ERROR AL GUARDAR - ' : ''}Nueva solicitud de configuración recibida:
        
        Cliente: ${orderData.customerName}
        Email: ${orderData.customerEmail}
        Teléfono: ${orderData.customerPhone}
        NIT: ${orderData.customerNIT}
        
        Producto Principal: ${orderData.selectedParent}
        Componentes Seleccionados: ${JSON.stringify(orderData.selectedChildren, null, 2)}
        
        Total: $${orderData.totalPrice}
        Notas: ${orderData.notes}
        
        ${orderData.cotiza_id ? `Cotización ID: ${orderData.cotiza_id}` : ''}
        ${orderData.cotiza_numero ? `Número de Cotización: ${orderData.cotiza_numero}` : ''}
        ${orderData.error ? `Error: ${orderData.error}` : ''}
        `
    };
    
    console.log('Order notification would be sent:', emailData);
    
    // Actually send the email using the email service
    try {
      const requestBody = {
        quotation: {
          numero: orderData.cotiza_numero || 'N/A',
          fecha: new Date().toISOString().split('T')[0],
          nombre: orderData.customerName,
          nit: orderData.customerNIT,
          telefono: orderData.customerPhone,
          correo: orderData.customerEmail,
          total: orderData.totalPrice
        },
        clientEmail: 'admin@nonosky.com', // Send to admin
        pdfBase64: '' // No PDF for notification emails
      };
      
      const response = await fetch(`${EMAIL_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Admin notification email sent successfully:', result.messageId);
      } else {
        console.error('❌ Failed to send admin notification email:', response.status);
      }
    } catch (emailError) {
      console.error('❌ Error sending admin notification email:', emailError);
    }
    
  } catch (error) {
    console.error('Error sending notification:', error);
  }
}

// Send customer confirmation email with PDF
async function sendCustomerConfirmation(orderData) {
  try {
    console.log('📧 Sending customer confirmation email to:', orderData.customerEmail);
    
    // Generate PDF for the quotation
    let pdfBase64 = null;
    try {
      const pdfBlob = await generateCheckoutPDF(orderData);
      pdfBase64 = await blobToBase64(pdfBlob);
      console.log('✅ PDF generated successfully for customer email');
    } catch (pdfError) {
      console.error('⚠️ Error generating PDF for customer email:', pdfError);
      // Continue without PDF if generation fails
    }
    
    const requestBody = {
      quotation: {
        numero: orderData.cotiza_numero || 'N/A',
        fecha: new Date().toISOString().split('T')[0],
        nombre: orderData.customerName,
        nit: orderData.customerNIT,
        telefono: orderData.customerPhone,
        correo: orderData.customerEmail,
        total: orderData.totalPrice
      },
      clientEmail: orderData.customerEmail, // Send to customer
      pdfBase64: pdfBase64 || '' // Include PDF if generated
    };
    
    console.log('Sending email request to:', `${EMAIL_API_URL}`);
    
    const response = await fetch(`${EMAIL_API_URL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });
    
    if (response.ok) {
      const result = await response.json();
      console.log('✅ Customer confirmation email sent successfully:', result.messageId);
    } else {
      const errorText = await response.text();
      console.error('❌ Failed to send customer confirmation email:', response.status, errorText);
    }
  } catch (emailError) {
    console.error('❌ Error sending customer confirmation email:', emailError);
  }
}

// Generate PDF for checkout order
async function generateCheckoutPDF(orderData) {
  try {
    // Create new PDF document
    const doc = new jsPDF('p', 'mm', 'a4');
    
    // Set up colors (Nonosky palette: black background with red accents)
    const colors = {
      primary: '#DC2626', // Red-600
      secondary: '#7F1D1D', // Red-800
      background: '#000000', // Black
      text: '#FFFFFF', // White
      textSecondary: '#D1D5DB' // Gray-300
    };

    // Add cover page first
    await addCoverPage(doc, colors);
    
    // Add new page for content
    doc.addPage();

    // Add background
    doc.setFillColor(colors.background);
    doc.rect(0, 0, 210, 297, 'F');

    // Add header with company branding
    await addHeader(doc, colors);
    
    // Prepare quotation object for PDF
    const quotation = {
      numero: orderData.cotiza_numero || 'N/A',
      fecha: new Date().toISOString().split('T')[0],
      nombre: orderData.customerName,
      nit: orderData.customerNIT,
      telefono: orderData.customerPhone,
      correo: orderData.customerEmail,
      total: orderData.totalPrice
    };
    
    // Prepare details array from order data
    const details = [];
    
    // Fetch parent product name if available
    let parentName = 'Servidor Base';
    if (orderData.selectedParent) {
      try {
        const parentProduct = await pb.collection('producto').getOne(orderData.selectedParent);
        parentName = parentProduct.nombre || 'Servidor Base';
      } catch (error) {
        console.warn('Could not fetch parent product name:', error);
      }
    }
    
    // Add parent product if available
    if (orderData.selectedParent) {
      details.push({
        expand: { producto: { nombre: parentName } },
        cantidad: 1,
        precio: 0,
        total: 0
      });
    }
    
    // Add child products with their names
    if (orderData.selectedChildren && orderData.selectedChildren.length > 0) {
      for (const child of orderData.selectedChildren) {
        // Use the name from the child object (already available from the form)
        details.push({
          expand: { producto: { nombre: child.nombre || 'Componente' } },
          cantidad: child.cantidad || 1,
          precio: child.precio || 0,
          total: (child.precio || 0) * (child.cantidad || 1)
        });
      }
    }
    
    // Add quotation information
    addQuotationInfo(doc, quotation, colors, details);
    
    // Add client information
    addClientInfo(doc, quotation, colors);
    
    // Add details table
    addDetailsTable(doc, details, colors);
    
    // Collect images from all products (parent + children)
    const allImages = [];
    
    // Get images from parent product
    if (orderData.selectedParent) {
      try {
        const parentImages = await getProductImages(orderData.selectedParent);
        parentImages.forEach(img => allImages.push(img.url));
      } catch (error) {
        console.warn('Could not fetch parent product images:', error);
      }
    }
    
    // Get images from child products
    if (orderData.selectedChildren && orderData.selectedChildren.length > 0) {
      for (const child of orderData.selectedChildren) {
        if (child.id) {
          try {
            const childImages = await getProductImages(child.id);
            childImages.forEach(img => allImages.push(img.url));
          } catch (error) {
            console.warn(`Could not fetch images for child product ${child.id}:`, error);
          }
        }
      }
    }
    
    // Add images section (always add, even if empty - function handles empty arrays)
    console.log('🖼️ Adding images to checkout PDF:', allImages.length, 'images');
    await addImages(doc, allImages, colors);
    
    // Add footer
    addFooter(doc, colors);
    
    // Return PDF as blob
    return doc.output('blob');
    
  } catch (error) {
    console.error('Error generating checkout PDF:', error);
    throw error;
  }
}

// Helper function to convert blob to base64
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1]; // Remove data:application/pdf;base64, prefix
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Get parent products (servers) for the form
export async function getParentProducts() {
  try {
    // Get all products and filter manually
    const response = await pb.collection('producto').getList(1, 1000, {
      sort: 'nombre',
      $autoCancel: false
    });
    
    // Filter products that don't have parents (servers)
    const parentProducts = response.items.filter(product => {
      // Product is a server if it has no parent (empty array or no parent)
      const hasNoParent = !product.parent || 
                         product.parent === "" || 
                         product.parent === null ||
                         product.parent === undefined ||
                         (Array.isArray(product.parent) && product.parent.length === 0);
      
      // Include active products or ASUS servers
      const isActive = product.sta !== false;
      const isASUSServer = product.nombre && product.nombre.includes('ASUS');
      
      return (hasNoParent && isActive) || isASUSServer;
    });
    
    return parentProducts;
  } catch (error) {
    console.error('Error loading parent products:', error);
    throw error;
  }
}

// Get child products for a selected parent
export async function getChildProducts(parentId) {
  try {
    if (!parentId) {
      return [];
    }

    console.log('🔍 Getting child products for parent ID:', parentId);
    
    // Try different filter approaches for the relation field
    let response;
    try {
      // First try: using the relation filter
      response = await pb.collection('producto').getList(1, 1000, {
        filter: `parent ~ "${parentId}"`,
        sort: 'nombre',
        $autoCancel: false
      });
      console.log('✅ Filter ~ worked, found:', response.items.length);
    } catch (filterError) {
      console.log('❌ Filter ~ failed, trying alternative:', filterError);
      try {
        // Second try: using exact match
        response = await pb.collection('producto').getList(1, 1000, {
          filter: `parent = "${parentId}"`,
          sort: 'nombre',
          $autoCancel: false
        });
        console.log('✅ Filter = worked, found:', response.items.length);
      } catch (secondError) {
        console.log('❌ Filter = failed, trying contains:', secondError);
        // Third try: using contains
        response = await pb.collection('producto').getList(1, 1000, {
          filter: `parent ?~ "${parentId}"`,
          sort: 'nombre',
          $autoCancel: false
        });
        console.log('✅ Filter ?~ worked, found:', response.items.length);
      }
    }
    
    const activeProducts = response.items.filter(product => product.sta !== false);
    console.log('🎯 Active child products:', activeProducts.length);
    
    // Log each product for debugging
    activeProducts.forEach((product, index) => {
      console.log(`📦 Child ${index + 1}:`, {
        id: product.id,
        nombre: product.nombre,
        parent: product.parent,
        sta: product.sta
      });
    });
    
    return activeProducts;
  } catch (error) {
    console.error('Error loading child products:', error);
    throw error;
  }
}

// Get categories for component filtering
export async function getCategories() {
  try {
    const response = await pb.collection('categoria').getList(1, 100, {
      sort: 'descrip',
      expand: 'parent',
      $autoCancel: false
    });
    
    return response.items.filter(category => category.sta !== false);
  } catch (error) {
    console.error('Error loading categories:', error);
    throw error;
  }
}

// Get product images
export async function getProductImages(productId) {
  try {
    console.log('🖼️ Loading images for product:', productId);
    console.log('🔍 Collection rules are already set to allow anonymous access');
    
    // Try to access the gallery collection
    const response = await pb.collection('producto_gallery').getList(1, 10, {
      filter: `producto="${productId}"`,
      sort: '-created',
      $autoCancel: false
    });
    
    console.log('✅ Images loaded successfully:', response.items.length);
    console.log('📋 Response details:', response);
    
    const images = response.items.map(item => {
      console.log('🖼️ Image item:', item);
      
      // Construct the full PocketBase file URL
      const baseUrl = pb.baseUrl;
      const fullUrl = `${baseUrl}/api/files/${item.collectionId}/${item.id}/${item.url}`;
      
      console.log('🔗 Constructed image URL:', fullUrl);
      
      return {
        id: item.id,
        url: fullUrl, // Use the full URL instead of just the filename
        collectionId: item.collectionId
      };
    });
    
    console.log('🎯 Final images for product:', productId, images.length);
    console.log('🖼️ Image URLs:', images.map(img => img.url));
    return images;
    
  } catch (error) {
    console.error('❌ Error loading product images:', error);
    console.log('🔍 Error details:', error.message);
    console.log('🔍 Error code:', error.status);
    
    // Return empty array if images can't be loaded
    return [];
  }
}

// Get child products with categories and images
export async function getChildProductsWithDetails(parentId) {
  try {
    if (!parentId) {
      return [];
    }

    console.log('🔍 Loading child products for parent:', parentId);
    
    // Try different filter approaches for the relation field
    let response;
    try {
      // First try: using the relation filter
      response = await pb.collection('producto').getList(1, 1000, {
        filter: `parent ~ "${parentId}"`,
        sort: 'nombre',
        expand: 'categoria_producto.categoria',
        $autoCancel: false
      });
      console.log('✅ Detailed filter ~ worked, found:', response.items.length);
    } catch (filterError) {
      console.log('❌ Detailed filter ~ failed, trying alternative:', filterError);
      try {
        // Second try: using exact match
        response = await pb.collection('producto').getList(1, 1000, {
          filter: `parent = "${parentId}"`,
          sort: 'nombre',
          expand: 'categoria_producto.categoria',
          $autoCancel: false
        });
        console.log('✅ Detailed filter = worked, found:', response.items.length);
      } catch (secondError) {
        console.log('❌ Detailed filter = failed, trying contains:', secondError);
        // Third try: using contains
        response = await pb.collection('producto').getList(1, 1000, {
          filter: `parent ?~ "${parentId}"`,
          sort: 'nombre',
          expand: 'categoria_producto.categoria',
          $autoCancel: false
        });
        console.log('✅ Detailed filter ?~ worked, found:', response.items.length);
      }
    }
    
    console.log('📦 Raw response:', response);
    console.log('📦 Items found:', response.items.length);
    
    const products = response.items.filter(product => product.sta !== false);
    console.log('✅ Active products:', products.length);
    
    // Get images for each product
    const productsWithImages = await Promise.all(
      products.map(async (product) => {
        const images = await getProductImages(product.id);
        console.log(`🖼️ Product ${product.nombre} has ${images.length} images`);
        return {
          ...product,
          images: images,
          firstImage: images.length > 0 ? images[0] : null
        };
      })
    );
    
    console.log('🎯 Final products with images:', productsWithImages.length);
    return productsWithImages;
  } catch (error) {
    console.error('Error loading child products with details:', error);
    throw error;
  }
}

// Debug function to see all products
export async function getAllProducts() {
  try {
    const response = await pb.collection('producto').getList(1, 100, {
      sort: 'nombre',
      $autoCancel: false
    });
    
    console.log('All products in database:', response.items.length);
    response.items.forEach((product, index) => {
      console.log(`Product ${index + 1}:`, {
        id: product.id,
        nombre: product.nombre,
        parent: product.parent,
        sta: product.sta,
        hasParent: !!product.parent,
        parentType: typeof product.parent
      });
    });
    
    return response.items;
  } catch (error) {
    console.error('Error loading all products:', error);
    throw error;
  }
}

// Debug function to test gallery collection access
export async function testGalleryAccess() {
  try {
    console.log('🔍 DEBUG: Testing gallery collection access...');
    const response = await pb.collection('producto_gallery').getList(1, 10);
    console.log('🔍 DEBUG: Gallery collection accessible:', response.items.length, 'items');
    console.log('🔍 DEBUG: Sample gallery item:', response.items[0]);
    return response.items;
  } catch (error) {
    console.error('❌ DEBUG: Gallery collection not accessible:', error);
    return [];
  }
}
